import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SubmitActions, AuthenticationSteps, type IUserInfoPrefillData } from "@features/Authentication";
import { type IDocument } from "@types";

interface ModalState {
  isPartnersModalOpen: boolean;
  identityVerificationModalState: {
    isOpen: boolean;
    redirectUrl?: string;
  };
  partnerModalIdentifier: string | null;
  authenticationModal: {
    step?: AuthenticationSteps;
    onSubmitCallback?: (response: Response, key: SubmitActions) => void;
    isEmailVerificationRequired?: boolean;
    prefillData?: IUserInfoPrefillData;
    lockedInPartner?: string | null;
  } | null;
  downloadBlobData?: string;
  landRegistrationModalState: {
    isOpen: boolean;
    document?: IDocument;
  };
  documentSignatureMethodSelectionModalState: {
    isOpen: boolean;
    document?: IDocument;
  };
  documentDigitalRegistrationModalState: {
    isOpen: boolean;
    document?: IDocument | null;
  };
}

const initialState: ModalState = {
  isPartnersModalOpen: false,
  identityVerificationModalState: {
    isOpen: false,
    redirectUrl: undefined,
  },
  partnerModalIdentifier: null,
  authenticationModal: null,
  downloadBlobData: undefined,
  landRegistrationModalState: {
    isOpen: false,
    document: undefined,
  },
  documentSignatureMethodSelectionModalState: {
    isOpen: false,
    document: undefined,
  },
  documentDigitalRegistrationModalState: {
    isOpen: false,
    document: null,
  },
};

const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    setPartnersModalOpen(state, action: PayloadAction<{ isOpen: boolean; identifier?: string }>) {
      state.isPartnersModalOpen = action.payload.isOpen;
      state.partnerModalIdentifier = action.payload.identifier == null ? null : action.payload.identifier;
    },
    setIdentityVerificationModalState(state, action: PayloadAction<ModalState["identityVerificationModalState"]>) {
      state.identityVerificationModalState = { ...action.payload };
    },
    setAuthenticationModalData(state, action: PayloadAction<ModalState["authenticationModal"]>) {
      state.authenticationModal = action.payload;
    },
    setDownloadBlobModal(state, action: PayloadAction<string | undefined>) {
      state.downloadBlobData = action.payload;
    },
    setLandRegistrationModalState(state, action: PayloadAction<ModalState["landRegistrationModalState"]>) {
      state.landRegistrationModalState = action.payload;
    },
    setDocumentSignatureMethodSelectionModalState(
      state,
      action: PayloadAction<ModalState["documentSignatureMethodSelectionModalState"]>,
    ) {
      state.documentSignatureMethodSelectionModalState = action.payload;
    },
    setDocumentDigitalRegistrationModalState(
      state,
      action: PayloadAction<ModalState["documentDigitalRegistrationModalState"]>,
    ) {
      state.documentDigitalRegistrationModalState = action.payload;
    },
  },
});

export const {
  setPartnersModalOpen,
  setIdentityVerificationModalState,
  setAuthenticationModalData,
  setDownloadBlobModal,
  setLandRegistrationModalState,
  setDocumentSignatureMethodSelectionModalState,
  setDocumentDigitalRegistrationModalState,
} = modalSlice.actions;
export type { ModalState };
export default modalSlice.reducer;
